/**
 * Send-report confirm dialog.
 *
 * Opens from the "Send to client" button on ReportDetail. Pre-fills the
 * To-list from `defaultRecipients` (organisation primary contact +
 * account owner) and lets staff edit before submitting.
 *
 * Local-only state — recipients live here until the parent's mutation
 * resolves. The parent owns the actual mutation + toast.
 */

import { useEffect, useState } from 'react';
import { X } from 'lucide-react';

import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';

interface Props {
  open: boolean;
  contractName: string;
  periodLabel: string;
  defaultRecipients: ReadonlyArray<string>;
  isSending: boolean;
  onClose: () => void;
  onSend: (recipients: string[]) => void;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function SendReportDialog({
  open,
  contractName,
  periodLabel,
  defaultRecipients,
  isSending,
  onClose,
  onSend,
}: Props) {
  const [recipients, setRecipients] = useState<string[]>([]);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Reset state every time the dialog opens — stale entries from a
  // previous session of the same dialog instance would surprise users.
  useEffect(() => {
    if (open) {
      setRecipients([...defaultRecipients]);
      setDraft('');
      setError(null);
    }
  }, [open, defaultRecipients]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSending) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, isSending, onClose]);

  if (!open) return null;

  // Compute what the final list would be if we sent right now —
  // shared by the Send button label and the actual submit handler so
  // the count and the action can never disagree.
  const trimmedDraft = draft.trim();
  const draftIsValid =
    trimmedDraft.length > 0 &&
    EMAIL_RE.test(trimmedDraft) &&
    !recipients.some(
      (r) => r.toLowerCase() === trimmedDraft.toLowerCase()
    );
  const effectiveRecipients = draftIsValid
    ? [...recipients, trimmedDraft]
    : recipients;

  function commitDraft(): boolean {
    if (!trimmedDraft) return true;
    if (!EMAIL_RE.test(trimmedDraft)) {
      setError(`"${trimmedDraft}" doesn't look like a valid email`);
      return false;
    }
    if (
      recipients.some((r) => r.toLowerCase() === trimmedDraft.toLowerCase())
    ) {
      setError(`${trimmedDraft} is already on the list`);
      return false;
    }
    setRecipients([...recipients, trimmedDraft]);
    setDraft('');
    setError(null);
    return true;
  }

  function removeRecipient(email: string) {
    setRecipients(recipients.filter((r) => r !== email));
  }

  function handleSend() {
    if (trimmedDraft && !draftIsValid) {
      // Surface why we won't accept it.
      commitDraft();
      return;
    }
    if (effectiveRecipients.length === 0) {
      setError('Add at least one recipient');
      return;
    }
    onSend(effectiveRecipients);
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 dialog-in-backdrop"
      onClick={isSending ? undefined : onClose}
    >
      <div
        className="w-full max-w-md mx-4 rounded-lg border border-ink-600 bg-ink-850 shadow-xl dialog-in-panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="send-report-title"
      >
        <header className="flex items-start justify-between px-5 pt-4 pb-3 border-b border-ink-700">
          <div>
            <h2
              id="send-report-title"
              className="text-[15px] font-semibold text-ink-100"
            >
              Send report
            </h2>
            <p className="text-[12.5px] text-ink-300 mt-0.5">
              {contractName} — {periodLabel}
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={isSending}
            className="text-ink-300 hover:text-ink-100 focus-ring rounded-md p-1 disabled:opacity-50"
            aria-label="Close dialog"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="px-5 py-4 space-y-3">
          <div>
            <label className="block text-[11px] uppercase tracking-[0.06em] text-ink-300 mb-1.5">
              Recipients
            </label>
            {recipients.length > 0 && (
              <ul className="flex flex-wrap gap-1.5 mb-2">
                {recipients.map((email) => (
                  <li
                    key={email}
                    className="inline-flex items-center gap-1.5 rounded-md bg-ink-800 border border-ink-600 pl-2 pr-1 py-0.5 text-[12.5px] text-ink-100 mono"
                  >
                    {email}
                    <button
                      onClick={() => removeRecipient(email)}
                      disabled={isSending}
                      aria-label={`Remove ${email}`}
                      className="text-ink-300 hover:text-ink-100 focus-ring rounded p-0.5 disabled:opacity-50"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <Input
              type="email"
              placeholder="add another email + Enter"
              value={draft}
              disabled={isSending}
              onChange={(e) => {
                setDraft(e.target.value);
                if (error) setError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ',') {
                  e.preventDefault();
                  commitDraft();
                } else if (
                  e.key === 'Backspace' &&
                  draft.length === 0 &&
                  recipients.length > 0
                ) {
                  // Quick-delete the last chip.
                  setRecipients(recipients.slice(0, -1));
                }
              }}
            />
            {error && (
              <p
                className="text-[12px] text-red-400 mt-1.5"
                role="alert"
              >
                {error}
              </p>
            )}
          </div>

          <p className="text-[12px] text-ink-300">
            The report will be emailed with the rendered HTML inline.
            Sending is one-way — once sent, the report is locked.
          </p>
        </div>

        <footer className="flex items-center justify-end gap-2 px-5 py-3 border-t border-ink-700">
          <Button
            variant="secondary"
            size="sm"
            onClick={onClose}
            disabled={isSending}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSend}
            disabled={isSending || effectiveRecipients.length === 0}
          >
            {isSending
              ? 'Sending…'
              : `Send to ${effectiveRecipients.length} ${
                  effectiveRecipients.length === 1
                    ? 'recipient'
                    : 'recipients'
                }`}
          </Button>
        </footer>
      </div>
    </div>
  );
}
